﻿import { ElementType, lazy, Suspense } from 'react';
import { Navigate, Outlet, useLocation, useRoutes } from 'react-router';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import { RoleAction } from 'src/Api/Enum/Role';
import ErrorBoundary from 'src/ErrorBoundary';
import AuthGuard from 'src/guards/AuthGuard';
import GuestGuard from 'src/guards/GuestGuard';
import useAuth from 'src/hooks/useAuth';
import LoadingScreen from '../components/LoadingScreen';
import { PATH_PRIVACY, PATH_WELCOME_PAGE } from './paths';
import ReactGA from 'react-ga4';
import { PrivacyProvider } from 'src/contexts/privacy/PrivacyContext';

import Login from '../pages/auth/Login';
import PageLegalDocs from 'src/pages/privacy/PageLegalDocs';
import PageQA from 'src/pages/privacy/PageQA';
import PageContactUs from 'src/pages/privacy/PageContactUs';

const w = window as any;
if (typeof window !== 'undefined' && !w.__dedupeHistoryPushPatched) {
  w.__dedupeHistoryPushPatched = true;

  const originalPushState = window.history.pushState.bind(window.history);

  window.history.pushState = (data: any, unused: string, url?: string | URL | null) => {
    if (url != null) {
      try {
        const target = new URL(url.toString(), window.location.href);
        const isSameUrl =
          target.pathname === window.location.pathname &&
          target.search === window.location.search &&
          target.hash === window.location.hash;

        if (isSameUrl) {
          window.history.replaceState(data, unused, url);
          return;
        }
      } catch {
      }
    }
    originalPushState(data, unused, url);
  };
}

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated, user } = useAuth();

  const splittedPath = pathname.split('/') as string[];

  const allowAnonymous = pathname.includes('auth');
  const isDashboard = isAuthenticated && !allowAnonymous;
  const is401 = isAuthenticated && pathname.split('/')[1] === '401';

  ReactGA.send({ hitType: 'pageview', name: pathname });

  if (!user || allowAnonymous || is401) {
    return (
      <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
        <Component {...props} />
      </Suspense>)
  }

  // check if user has required role to access the page based on the first node of the pathname
  const { permission } = user;
  if (!permission) return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Component {...props} />
    </Suspense>
  );

  const permissionKeys = Object.keys(permission);

  const enabledPath = splittedPath && splittedPath.find(x =>
    permissionKeys.some(pk => pk.toLowerCase() === x.toLowerCase().replace(/-/g, '_'))
  );

  const userPathEnable = enabledPath && permission[
    permissionKeys.find(pk => pk.toLowerCase() === enabledPath.toLowerCase().replace(/-/g, '_'))!
  ]?.find((x: any) => x.action.includes(RoleAction.VIEW));

  if (!userPathEnable) {
    window.location.href = '/401';
    return null;
  }

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <ErrorBoundary>
        <Component {...props} />
      </ErrorBoundary>
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'sent-email', element: <SentEmail /> },
        { path: 'registration', element: <Registration /> },
        { path: 'request-join', element: <RequestJoin /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'token-expired', element: <TokenExpired /> },
        { path: 'password-changed', element: <PasswordChanged /> },
        { path: 'registration-complete', element: <RegistrationComplete /> },
        { path: 'login-success', element: <GuestGuard><LoginSuccess /></GuestGuard> },
        { path: 'login-error', element: <LoginError /> },
        { path: 'mfa', element: <GuestGuard><MfaPage /></GuestGuard> },
        { path: 'refresh-error', element: <RefreshError /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_WELCOME_PAGE} replace />, index: true },
        { path: 'welcomePage', element: <PageWelcomePage /> },
        { path: `displayView/retail/:id/:section/:pageName`, element: <PageDisplayViewContainer /> },
        { path: `displayView/retail/:id/:section`, element: <PageDisplayViewContainer /> },
        { path: `displayView/ooh/:id/:section/:pageName`, element: <PageDisplayViewContainer /> },
        { path: `displayView/ooh/:id/:section`, element: <PageDisplayViewContainer /> },

        { path: `displayView/checkSensor/:sensorId/:section/:pageName`, element: <PageDisplayViewCheckSensorContainer /> },
        { path: `displayView/checkSensor/:sensorId/:section`, element: <PageDisplayViewCheckSensorContainer /> },
        { path: 'mapLocation', element: <PageMapLocation /> },
        { path: 'customerOverview', element: <PageCustomerOverview /> },

        { path: 'pom', element: <PagePom /> },
        { path: 'pom/:id', element: <PagePom /> },
        { path: 'pom/:id/edit', element: <PagePom /> },

        { path: 'ticketing', element: <PageTicketing /> },
        { path: 'ticketing/:id/sensor', element: <PageTicketingByPom /> },
        { path: 'ticketing/:id/location', element: <PageTicketingByLocation /> },

        { path: 'location-status/:area', element: <PageLocationStatusContent /> },
        { path: 'location-status', element: <PageLocationStatusContent /> },

        {
          path: 'privacy_section',
          element: (
            <PrivacyProvider>
              <Outlet />
            </PrivacyProvider>
          ),
          children: [
            { element: <Navigate to={PATH_PRIVACY.legalDocs} replace />, index: true },
            { path: 'legal-docs', element: <PageLegalDocs /> },
            { path: 'qa', element: <PageQA /> },
            { path: 'contact-us', element: <PageContactUs /> },
          ],
        },

        {
          path: 'manage',
          children: [
            { element: <Navigate to={PATH_WELCOME_PAGE} replace />, index: true },
            {
              path: 'location',
              children: [
                { element: <Navigate to="/dashboard/manage/location/location" replace />, index: true },
                { path: 'location', element: <PageLocation /> },
                { path: 'location/:id', element: <PageLocation /> },
                { path: 'location/:id/edit', element: <PageLocation /> },

                { path: 'city', element: <PageCity /> },
                { path: 'city/:id', element: <PageCity /> },
                { path: 'city/:id/edit', element: <PageCity /> },

                { path: 'province', element: <PageProvince /> },
                { path: 'province/:id', element: <PageProvince /> },
                { path: 'province/:id/edit', element: <PageProvince /> },

                { path: 'region', element: <PageRegion /> },
                { path: 'region/:id', element: <PageRegion /> },
                { path: 'region/:id/edit', element: <PageRegion /> },
              ],
            },
            {
              path: 'sensor',
              children: [
                { element: <Navigate to="/dashboard/manage/head-counter" replace />, index: true },
                { path: 'head-counter', element: <PageHeadCounter /> },
                { path: 'head-counter/:id', element: <PageHeadCounter /> },
                { path: 'head-counter/:id/edit', element: <PageHeadCounter /> },
                { path: 'head-counter/:sensorId/frames', element: <PageHeadCounterFrames /> },

                { path: 'pom', element: <PagePom /> },
                { path: 'pom/:id', element: <PagePom /> },
                { path: 'pom/:id/edit', element: <PagePom /> },

                { path: 'softwareVersion', element: <PageSoftwareVersion /> },
                { path: 'softwareVersion/:id', element: <PageSoftwareVersion /> },
                { path: 'softwareVersion/:id/edit', element: <PageSoftwareVersion /> },

                { path: 'event', element: <PageEvent /> },
                { path: 'event/:id', element: <PageEvent /> },
                { path: 'event/:id/edit', element: <PageEvent /> },
              ]
            },
            {
              path: 'tag-category',
              children: [
                { element: <Navigate to="/dashboard/manage/tag-category" replace />, index: true },
                { path: 'category', element: <PageCategory /> },
                { path: 'category/:id', element: <PageCategory /> },
                { path: 'category/:id/edit', element: <PageCategory /> },

                { path: 'tag', element: <PageTag /> },
                { path: 'tag/:id', element: <PageTag /> },
                { path: 'tag/:id/edit', element: <PageTag /> },
              ]
            },
            {
              path: 'view',
              children: [
                { element: <Navigate to="/dashboard/manage/view" replace />, index: true },
                { path: 'view', element: <PageView /> },
                { path: 'view/:id', element: <PageView /> },
                { path: 'view/:id/edit', element: <PageView /> },

                { path: 'dashboardLayout', element: <PageDashboardLayout /> },
                { path: 'dashboardLayout/:id', element: <PageDashboardLayout /> },
                { path: 'dashboardLayout/:id/edit', element: <PageDashboardLayout /> },
              ]
            },
            {
              path: 'customers',
              children: [
                { element: <Navigate to="/dashboard/manage/customers" replace />, index: true },
                { path: 'user', element: <PageUser /> },
                { path: 'user/:id', element: <PageUser /> },
                { path: 'user/:id/edit', element: <PageUser /> },

                { path: 'usergroup', element: <PageUserGroup /> },

                { path: 'industry', element: <PageIndustry /> },
                { path: 'industry/:id', element: <PageIndustry /> },
                { path: 'industry/:id/edit', element: <PageIndustry /> },

                { path: 'customer', element: <PageCustomer /> },
                { path: 'customer/:id', element: <PageCustomer /> },
                { path: 'customer/:id/edit', element: <PageCustomer /> },
              ]
            },
          ]
        },
        {
          path: 'profile',
          children: [
            { element: <Navigate to="/dashboard/profile/profile" replace />, index: true },
            { path: 'profile', element: <PageProfile /> },
            { path: 'change-password', element: <PageChangePassword /> },
          ]
        }
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '401', element: <Unauthorized /> },
        { path: '*', element: <Navigate to="/dashboard" replace /> },
      ],
    },
  ]);
}

// AUTHENTICATION
/*
* changed from 
* const Login = Loadable(lazy(() => import('../pages/auth/Login')));
* to this because src/guards/AuthGuard.tsx import it statically and throw warning: 
* [INEFFECTIVE_DYNAMIC_IMPORT] Warning: src/pages/auth/Login.tsx is dynamically imported by src/routes/index.tsx but also statically imported by src/guards/AuthGuard.tsx, dynamic import will not move module into another chunk.
*/

const LoginSuccess = Loadable(lazy(() => import('../pages/auth/LoginSuccess')));
const LoginError = Loadable(lazy(() => import('../pages/auth/LoginError')));
const RefreshError = Loadable(lazy(() => import('../pages/auth/RefreshError')));
const MfaPage = Loadable(lazy(() => import('../pages/auth/MfaPage')));
const RequestJoin = Loadable(lazy(() => import('../pages/auth/RequestJoin')));
const Registration = Loadable(lazy(() => import('../pages/auth/Registration')));
const SentEmail = Loadable(lazy(() => import('../pages/auth/SentEmail')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const TokenExpired = Loadable(lazy(() => import('../pages/auth/TokenExpired')));
const PasswordChanged = Loadable(lazy(() => import('../pages/auth/PasswordChanged')));
const RegistrationComplete = Loadable(lazy(() => import('../pages/auth/RegistrationComplete')));

// Dashboard
const PageWelcomePage = Loadable(lazy(() => import('../pages/welcomePage/PageWelcomePage')));
const PageDisplayViewContainer = Loadable(lazy(() => import('../pages/view/Header/PageDisplayViewContainer')));
const PageDisplayViewCheckSensorContainer = Loadable(lazy(() => import('../pages/ViewCheckSensor/PageDisplayViewCheckSensorContainer')));
const PageLocationStatusContent = Loadable(lazy(() => import('../pages/manager/PageLocationStatusContent')));

const PageMapLocation = Loadable(lazy(() => import('../pages/mapLocation/PageMapLocation')));

const PageCustomerOverview = Loadable(lazy(() => import('../pages/PageCustomerOverview')));

// userManager
const PageCity = Loadable(lazy(() => import('../pages/manager/PageArea')));
const PageView = Loadable(lazy(() => import('../pages/manager/PageView')));
const PageCustomer = Loadable(lazy(() => import('../pages/manager/PageCustomer')));
const PageIndustry = Loadable(lazy(() => import('../pages/manager/PageIndustry')));
const PageDashboardLayout = Loadable(lazy(() => import('../pages/manager/PageDashboardLayout')));
const PageEvent = Loadable(lazy(() => import('../pages/manager/PageEvent')));
const PagePom = Loadable(lazy(() => import('../pages/manager/PagePom')));
const PageCategory = Loadable(lazy(() => import('../pages/manager/PageCategory')));
const PageTag = Loadable(lazy(() => import('../pages/manager/PageTag')));
const PageTicketing = Loadable(lazy(() => import('../pages/manager/PageTicketing')));
const PageTicketingByPom = Loadable(lazy(() => import('../pages/manager/PageTicketingBySensor')));
const PageTicketingByLocation = Loadable(lazy(() => import('../pages/manager/PageTicketingByLocation')));
const PageHeadCounter = Loadable(lazy(() => import('../pages/manager/PageHeadcounterSensor')));
const PageHeadCounterFrames = Loadable(lazy(() => import('../pages/manager/PageHeadcounterSensorFrames')));
const PageLocation = Loadable(lazy(() => import('../pages/manager/PageLocation')));
const PageProvince = Loadable(lazy(() => import('../pages/manager/PageProvince')));
const PageRegion = Loadable(lazy(() => import('../pages/manager/PageRegion')));
const PageSoftwareVersion = Loadable(lazy(() => import('../pages/manager/PageSoftwareVersion')));
const PageUser = Loadable(lazy(() => import('../pages/manager/PageUser')));
const PageUserGroup = Loadable(lazy(() => import('../pages/manager/PageUserGroup')));

// Profile
const PageProfile = Loadable(lazy(() => import('../pages/profile/PageProfile')));
const PageChangePassword = Loadable(lazy(() => import('../pages/profile/PageChangePassword')));

const NotFound = Loadable(lazy(() => import('../pages/settings/Page404')));
const Unauthorized = Loadable(lazy(() => import('../pages/settings/Page403')));